import React, { useEffect } from 'react';
import { Box, Divider, LinearProgress, Table, TableBody, TableHead, TableRow } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useHistory } from 'react-router-dom';
import axios from 'axios';
import Chart from 'react-apexcharts';
import { useMediaQuery } from 'react-responsive';
import StarOutlineIcon from '@material-ui/icons/StarOutline';
import StarIcon from '@material-ui/icons/Star';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos';
import { storeContext } from '../provider/Provider';
import { websiteAnalyticsActions } from '../../store/website-analytics-reducer';
import chartOptions from '../../customUI/ChartOptions';
import { leadsActions } from '../../store/leads-reducer';
import {
  StyledContentTableCell,
  StyledContentTableHeadCell,
  StyledContentTableHeadCell1,
  StyledContentTableHeadCell2,
} from '../../customUI/table/TableStyles';
import MobileTitle from '../../customUI/MobileTitle';
import { dropTrailingEmptyMonths } from '../../functions';

const { REACT_APP_FLASK_API_URL } = process.env;

const useStyles = makeStyles((theme) => ({
  toolbar: theme.mixins.toolbar,
  lead__flex: {
    display: 'flex',
  },
  flex__item: {
    flexGrow: 1,
    margin: 15,
    width: '30%',
  },
  flex__item1: {
    flexGrow: 2,
    margin: 15,
    width: '30%',
  },
  colorPrimary: {
    backgroundColor: '#E5E5E5',
  },
  barColorPrimary: {
    backgroundColor: '#109CF1',
  },
  wrapper: {
    margin: 20,
    [theme.breakpoints.down('sm')]: {
      margin: '0 5px',
    },
  },
  card: {
    padding: 15,
    background: 'white',
    borderRadius: '16px',
    boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
    [theme.breakpoints.down('sm')]: {
      padding: 5,
      margin: 10,
      marginBottom: 15,
    },
  },
  heading: {
    padding: '0 0 10px 16px',
    fontSize: 30,
    fontWeight: 700,
    color: '#58595B',
    [theme.breakpoints.down('sm')]: {
      fontSize: 18,
      padding: '0 0 5px 5px',
    },
  },
  leadsCount: {
    fontSize: '4rem',
    fontWeight: 700,
    lineHeight: 1,
  },
  card__wrapper: {
    padding: '0 10px',
    alignSelf: 'center',
  },
  table__card: {
    padding: '10px 0',
    background: 'white',
    borderRadius: '16px',
    boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
    [theme.breakpoints.down('sm')]: {
      margin: 10,
      marginBottom: 15,
    },
  },
  noContentBlock: {
    minHeight: 200,
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  cardActionButton: {
    padding: '15px 0 5px 0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    fontSize: '14px',
    cursor: 'pointer',
  },
  viewAllText: {
    fontSize: 15,
    userSelect: 'none',
  },
  keywordsEnd: {
    display: 'flex',
    '&>p': {
      alignSelf: 'center',
      width: '80%',
      paddingLeft: 16,
    },
  },
}));

const Analytics = () => {
  const isPhone = useMediaQuery({ query: '(max-width: 768px)' });
  const { state, dispatch } = React.useContext(storeContext);
  const classes = useStyles();
  const history = useHistory();
  const [keywordsTableType, setKeywordsTableType] = React.useState('starred');

  useEffect(() => {
    dispatch({
      type: websiteAnalyticsActions.WEBSITE_ANALYTICS_SET_IS_FETCHING,
      payload: { isFetching: true },
    });
    axios
      .get(`${REACT_APP_FLASK_API_URL}/v1/analytics/${state.websiteSwitch.currentWebsite}`)
      .then((res) => {
        if (!res.data.error) {
          dispatch({
            type: websiteAnalyticsActions.WEBSITE_ANALYTICS_SET_ALL,
            payload: {
              goal_completions: res.data.data.goal_completions,
              dataStudioReport: res.data.data.data_studio_report,
            },
          });
        }
      });
    axios
      .get(`${REACT_APP_FLASK_API_URL}/v1/analytics/monthly/${state.websiteSwitch.currentWebsite}`)
      .then((res) => {
        if (!res.data.error || res.data.message.includes('Unknown error 500')) {
          dispatch({
            type: websiteAnalyticsActions.WEBSITE_ANALYTICS_SET_ALL,
            payload: {
              history: dropTrailingEmptyMonths(res.data.data),
              isFetching: false,
            },
          });
        }
      });
    axios
      .get(`${REACT_APP_FLASK_API_URL}/v1/leads/${state.websiteSwitch.currentWebsite}/count`)
      .then((res) => {
        dispatch({
          type: leadsActions.LEADS_SET_ALL,
          payload: {
            currentPeriodLeadsCount: res.data.data.leadsCurrentMonth,
          },
        });
      });
    axios
      .get(
        `${REACT_APP_FLASK_API_URL}/v1/get-call-metrics-totals/${state.websiteSwitch.currentWebsite}`
      )
      .then((res) => {
        dispatch({
          type: leadsActions.LEADS_SET_ALL,
          payload: {
            currentPeriodCallsCount: res.data.callsCurrentMonth,
          },
        });
      });
    axios
      .get(
        `${REACT_APP_FLASK_API_URL}/v1/get-avg-keywords-rankings/${state.websiteSwitch.currentWebsite}/top5/1/10`
      )
      .then((res) => {
        if (res.data.success) {
          if (res.data.message !== 'No results') {
            setKeywordsTableType(res.data.type);
            dispatch({
              type: websiteAnalyticsActions.WEBSITE_ANALYTICS_SET_ALL,
              payload: {
                keywordRankings: {
                  keywords: res.data.message,
                },
              },
            });
          }
        }
      });
  }, []);
  const barChartOptions = {
    ...chartOptions,
    labels: state.websiteAnalytics.sessionsLabels,
    xaxis: {
      labels: {
        formatter: (w) => {
          if (w.length > 1) {
            return w.split(' ');
          }
          return w;
        },
      },
    },
    tooltip: {
      x: { show: false },
      y: {
        title: {
          formatter: () => '',
        },
      },
    },
  };
  barChartOptions.colors = ['#5AA600'];
  barChartOptions.plotOptions.bar.columnWidth = '40%';
  barChartOptions.plotOptions.bar.borderRadius = 2;
  const donutChartOptions = { ...chartOptions, labels: ['Forms', 'Calls'] };
  donutChartOptions.colors = ['#2A5CA9', '#B83A94', '#00B5E2'];
  const channelLabels = state.websiteAnalytics.conversionsByChannelLabels;
  channelLabels.forEach((n, indx) => {
    if (n === '(none)') {
      channelLabels[indx] = 'Direct';
    }
  });
  const conversionsByChannelChart = {
    ...chartOptions,
    labels: channelLabels,
  };
  conversionsByChannelChart.colors = ['#FFA400', '#00B5E2', '#5AA600', '#B83A94', '#2A5CA9'];
  const handleViewAllButtonClick = () => {
    history.push('/keywords/full-view');
  };

  function sanitise(x) {
    if (Number.isNaN(Number(x)) || x === 0) {
      return '-';
    }
    return x;
  }

  const handleStar = (keyword) => {
    axios
      .post(
        `${REACT_APP_FLASK_API_URL}/v1/update-starred-keyword/${state.websiteSwitch.currentWebsite}`,
        { keyword }
      )
      .then((res) => {
        dispatch({
          type: websiteAnalyticsActions.WEBSITE_ANALYTICS_STAR,
          payload: {
            keyword: res.data.keyword,
            starred: res.data.starred,
          },
        });
      });
  };
  if (state.websiteAnalytics.isFetching) {
    return (
      <LinearProgress
        style={{ width: '100%' }}
        classes={{
          colorPrimary: classes.colorPrimary,
          barColorPrimary: classes.barColorPrimary,
        }}
      />
    );
  }

  if (isPhone) {
    return (
      <>
        <MobileTitle title="Analytics" />
        <div className={classes.wrapper}>
          {!state.websiteAnalytics.dataStudioReport && !state.websiteAnalytics.isTesting && (
            <>
              <div className={classes.card}>
                <div className={classes.heading}>Organic Traffic Trend</div>
                <Chart
                  options={barChartOptions}
                  series={state.websiteAnalytics.sessionsValues}
                  type="bar"
                  height="250"
                />
              </div>
              <div className={classes.card}>
                <div className={classes.heading}>Organic Leads</div>
                <p style={{ padding: '0 0 10px 16px', margin: 0, fontWeight: 400 }}>
                  *Past 4 weeks
                </p>
                <Chart
                  options={donutChartOptions}
                  series={[
                    state.leads.currentPeriodLeadsCount,
                    state.leads.currentPeriodCallsCount,
                  ]}
                  type="donut"
                  height="298"
                />
              </div>
            </>
          )}
          {!state.websiteAnalytics.dataStudioReport && state.websiteAnalytics.keywordRankings.keywords.length > 0 && (
            <div className={classes.table__card}>
              <div className={classes.card__wrapper}>
                <div className={classes.heading}>
                  {keywordsTableType === 'starred' ? (
                    <>Starred Keywords</>
                  ) : (
                    <>Biggest Keyword Movements</>
                  )}
                </div>
              </div>
              <div className={classes.card__wrapper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <StyledContentTableHeadCell1 />
                      <StyledContentTableHeadCell>Keyword</StyledContentTableHeadCell>
                      <StyledContentTableHeadCell>
                        Position Current Month
                      </StyledContentTableHeadCell>
                      <StyledContentTableHeadCell>
                        Position Previous Month
                      </StyledContentTableHeadCell>
                      <StyledContentTableHeadCell2>Change</StyledContentTableHeadCell2>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {state.websiteAnalytics.keywordRankings.keywords.length > 0 &&
                      state.websiteAnalytics.keywordRankings.keywords.map((row) => (
                        <TableRow>
                          <StyledContentTableCell component="th" scope="row">
                            {!row.starred ? (
                              <StarOutlineIcon
                                style={{ fontSize: 25, color: '#58595B', cursor: 'pointer' }}
                                onClick={() => {
                                  handleStar(row.keyword);
                                }}
                              />
                            ) : (
                              <StarIcon
                                style={{ fontSize: 25, color: '#ffa400', cursor: 'pointer' }}
                                onClick={() => {
                                  handleStar(row.keyword);
                                }}
                              />
                            )}
                          </StyledContentTableCell>
                          <StyledContentTableCell component="th" scope="row">
                            {row.keyword}
                          </StyledContentTableCell>
                          <StyledContentTableCell component="th" scope="row">
                            {row.current_month_position}
                          </StyledContentTableCell>
                          <StyledContentTableCell component="th" scope="row">
                            {row.previous_month_position}
                          </StyledContentTableCell>
                          <StyledContentTableCell component="th" scope="row">
                            {row.current_month_position === '-' ||
                            row.previous_month_position === '-' ? (
                              <span>&nbsp;&nbsp;&nbsp;-</span>
                            ) : (
                              <>
                                {row.current_month_position - row.previous_month_position >= 0 ? (
                                  <span style={{ color: '#D81F27', fontWeight: 600 }}>
                                    <ArrowDownwardIcon style={{ fontSize: 15 }} />
                                    {sanitise(
                                      Math.abs(
                                        row.current_month_position - row.previous_month_position
                                      )
                                    )}
                                  </span>
                                ) : (
                                  <span style={{ color: '#5AA600', fontWeight: 600 }}>
                                    <ArrowUpwardIcon style={{ fontSize: 15 }} />
                                    {sanitise(
                                      Math.abs(
                                        row.current_month_position - row.previous_month_position
                                      )
                                    )}
                                  </span>
                                )}
                              </>
                            )}
                          </StyledContentTableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </div>
              {state.websiteAnalytics.keywordRankings.keywords !== undefined ? (
                state.websiteAnalytics.keywordRankings.keywords.length === 0 && (
                  <div className={classes.noContentBlock}>
                    <span>No Content</span>
                  </div>
                )
              ) : (
                <div className={classes.noContentBlock}>
                  <span>No Content</span>
                </div>
              )}
              <Divider />
              <div className={classes.keywordsEnd}>
                <p>
                  Source:{' '}
                  <a href="https://www.semrush.com/" target="_blank" rel="noreferrer">
                    Semrush
                  </a>
                </p>
                <div className={classes.card__wrapper}>
                  <div
                    className={classes.cardActionButton}
                    onClick={handleViewAllButtonClick}
                    aria-hidden="true"
                    aria-label="viewAllBtn"
                  >
                    <div className={classes.viewAllText}>View All</div>
                    <ArrowForwardIosIcon style={{ paddingLeft: 10, fontSize: '11px' }} />
                  </div>
                </div>
              </div>
            </div>
          )}
          {state.websiteAnalytics.dataStudioReport?.length > 0 && <Box pb={2}>
            <div className={classes.card}>
              <div className={classes.card__wrapper} style={{ height: '90vh'}}>
                <iframe title='data-studio' width='100%' style={
                  {
                    display: 'block',
                    position: 'relative',
                    border: 'none',
                  }
                } height='100%' src={state.websiteAnalytics.dataStudioReport} />
              </div>
            </div>
          </Box>}
          {!state.websiteAnalytics.dataStudioReport && <div className={classes.card}>
            <div className={classes.heading}>Conversions by Channel</div>
            {!state.websiteAnalytics.isTesting && (
              <Chart
                options={conversionsByChannelChart}
                series={state.websiteAnalytics.conversionsByChannelSeries}
                type="donut"
                height="300"
              />
            )}
          </div>}
        </div>
      </>
    );
  }

  return (
    <>
      <div className={classes.wrapper}>
        <div className={classes.lead__flex}>
          {!state.websiteAnalytics.dataStudioReport && !state.websiteAnalytics.isTesting && (
            <>
              <div className={classes.flex__item1}>
                <div className={classes.card}>
                  <div className={classes.heading}>Organic Traffic Trend</div>
                  <Chart
                    options={barChartOptions}
                    series={state.websiteAnalytics.sessionsValues}
                    type="bar"
                    height="250"
                  />
                </div>
              </div>
              <div className={classes.flex__item}>
                <div className={classes.card}>
                  <div className={classes.card__wrapper}>
                    <div className={classes.heading}>Organic Leads</div>
                    <p style={{ padding: '0 0 10px 16px', margin: 0, fontWeight: 400 }}>
                      *Past 4 weeks
                    </p>
                    <Chart
                      options={donutChartOptions}
                      series={[
                        state.leads.currentPeriodLeadsCount,
                        state.leads.currentPeriodCallsCount,
                      ]}
                      type="donut"
                      height="298"
                    />
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        <div className={classes.lead__flex}>
          {!state.websiteAnalytics.dataStudioReport && state.websiteAnalytics.keywordRankings.keywords.length > 0 && (
            <div className={classes.flex__item}>
              <div className={classes.table__card}>
                <div className={classes.card__wrapper}>
                  <div className={classes.heading}>
                    {keywordsTableType === 'starred' ? (
                      <>Starred Keywords</>
                    ) : (
                      <>Biggest Keyword Movements</>
                    )}
                  </div>
                </div>
                <div className={classes.card__wrapper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <StyledContentTableHeadCell1 />
                        <StyledContentTableHeadCell>Keyword</StyledContentTableHeadCell>
                        <StyledContentTableHeadCell>
                          Position Current Month
                        </StyledContentTableHeadCell>
                        <StyledContentTableHeadCell>
                          Position Previous Month
                        </StyledContentTableHeadCell>
                        <StyledContentTableHeadCell2>Change</StyledContentTableHeadCell2>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {state.websiteAnalytics.keywordRankings.keywords.length > 0 &&
                        state.websiteAnalytics.keywordRankings.keywords.map((row) => (
                          <TableRow key={row}>
                            <StyledContentTableCell component="th" scope="row">
                              {!row.starred ? (
                                <StarOutlineIcon
                                  style={{ fontSize: 25, color: '#58595B', cursor: 'pointer' }}
                                  onClick={() => {
                                    handleStar(row.keyword);
                                  }}
                                />
                              ) : (
                                <StarIcon
                                  style={{ fontSize: 25, color: '#ffa400', cursor: 'pointer' }}
                                  onClick={() => {
                                    handleStar(row.keyword);
                                  }}
                                />
                              )}
                            </StyledContentTableCell>
                            <StyledContentTableCell component="th" scope="row">
                              {row.keyword}
                            </StyledContentTableCell>
                            <StyledContentTableCell component="th" scope="row">
                              {row.current_month_position}
                            </StyledContentTableCell>
                            <StyledContentTableCell component="th" scope="row">
                              {row.previous_month_position}
                            </StyledContentTableCell>
                            <StyledContentTableCell component="th" scope="row">
                              {row.current_month_position === '-' ||
                              row.previous_month_position === '-' ? (
                                <span>&nbsp;&nbsp;&nbsp;-</span>
                              ) : (
                                <>
                                  {row.current_month_position - row.previous_month_position >= 0 ? (
                                    <span style={{ color: '#D81F27', fontWeight: 600 }}>
                                      <ArrowDownwardIcon style={{ fontSize: 15 }} />
                                      {sanitise(
                                        Math.abs(
                                          row.current_month_position - row.previous_month_position
                                        )
                                      )}
                                    </span>
                                  ) : (
                                    <span style={{ color: '#5AA600', fontWeight: 600 }}>
                                      <ArrowUpwardIcon style={{ fontSize: 15 }} />
                                      {sanitise(
                                        Math.abs(
                                          row.current_month_position - row.previous_month_position
                                        )
                                      )}
                                    </span>
                                  )}
                                </>
                              )}
                            </StyledContentTableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </div>
                {state.websiteAnalytics.keywordRankings.keywords !== undefined ? (
                  <>
                    {state.websiteAnalytics.keywordRankings.keywords.length === 0 && (
                      <div className={classes.noContentBlock}>
                        <span>No Content</span>
                      </div>
                    )}
                  </>
                ) : (
                  <div className={classes.noContentBlock}>
                    <span>No Content</span>
                  </div>
                )}
                <Divider />
                <div className={classes.keywordsEnd}>
                  <p>
                    Source:{' '}
                    <a href="https://www.semrush.com/" target="_blank" rel="noreferrer">
                      Semrush
                    </a>
                  </p>
                  <div className={classes.card__wrapper}>
                    <div
                      className={classes.cardActionButton}
                      onClick={handleViewAllButtonClick}
                      aria-hidden="true"
                      aria-label="viewAllBtn"
                    >
                      <div className={classes.viewAllText}>View All</div>
                      <ArrowForwardIosIcon style={{ paddingLeft: 10, fontSize: '11px' }} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          <Box display='flex' flex={1} flexDirection='column'>
            {state.websiteAnalytics.dataStudioReport?.length > 0  && <Box pb={2}>
              <div className={classes.card}>
                <div className={classes.card__wrapper} style={{ height: '90vh'}}>
                  <iframe title='data-studio-desktop' width='100%' style={
                    {
                      display: 'block',
                      position: 'relative',
                      border: 'none',
                    }
                  } height='100%' src={state.websiteAnalytics.dataStudioReport} />
                </div>
              </div>
            </Box>}
            {!state.websiteAnalytics.dataStudioReport && <div >
              <div className={classes.card}>
                <div className={classes.card__wrapper}>
                  <div className={classes.heading}>Conversions by Channel</div>
                  {!state.websiteAnalytics.isTesting && (
                    <Chart
                      options={conversionsByChannelChart}
                      series={state.websiteAnalytics.conversionsByChannelSeries}
                      type="donut"
                      height="300"
                    />
                  )}
                </div>
              </div>
            </div>}
          </Box>
        </div>
      </div>
    </>
  );
};

export default Analytics;
